'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ChevronUp, Clock, TrendingUp } from 'lucide-react';

const GOSSIP_DATA = [
  {
    id: 'chisme-001',
    text: 'El marido de mi amiga dice que trabaja hasta tarde pero lo vieron en el mall con otra señora comprando ropa de mujer... para él 💅',
    category: 'Familia',
    votes: 445,
    status: 'trending',
    statusLabel: '🔥 Trending',
    time: 'hace 2 horas',
  },
  {
    id: 'chisme-002',
    text: 'El dueño del negocio de la esquina tiene dos familias en la misma cuadra y ninguna sabe de la otra 🤯',
    category: 'Vecinos',
    votes: 567,
    status: 'trending',
    statusLabel: '🔥 Top #1',
    time: 'hace 4 horas',
  },
  {
    id: 'chisme-003',
    text: 'Una amiga se fue de vacaciones con su "primo" y resulta que el primo tiene el mismo apellido que ella pero no son familia 🤔',
    category: 'Amor',
    votes: 312,
    status: 'en-cola',
    statusLabel: '⏳ En Cola',
    time: 'hace 5 horas',
  },
  {
    id: 'chisme-004',
    text: 'Mi vecina del 3ro lleva 3 semanas diciéndole a todos que está a dieta pero la pillé comprando 2 tortas en la pastelería... 👀',
    category: 'Vecinos',
    votes: 234,
    status: 'pendiente',
    statusLabel: '⏳ Pendiente',
    time: 'hace 6 horas',
  },
  {
    id: 'chisme-005',
    text: 'Mi jefe llegó a la reunión con la camisa al revés y nadie le dijo nada en toda la mañana 😂',
    category: 'Trabajo',
    votes: 189,
    status: 'contado',
    statusLabel: '✅ Contado en vivo',
    time: 'hace 1 día',
  },
  {
    id: 'chisme-006',
    text: 'Mi compañera de trabajo lleva 6 meses diciéndole a todos que está embarazada para no hacer nada... y no está embarazada 😱',
    category: 'Trabajo',
    votes: 156,
    status: 'pendiente',
    statusLabel: '⏳ Pendiente',
    time: 'hace 8 horas',
  },
];

const categoryColors: Record<string, string> = {
  Amor: 'bg-pink-100 text-pink-700',
  Trabajo: 'bg-blue-100 text-blue-700',
  Familia: 'bg-orange-100 text-orange-700',
  Vecinos: 'bg-green-100 text-green-700',
  Famosos: 'bg-purple-100 text-purple-700',
  Otro: 'bg-gray-100 text-gray-700',
};

const statusStyles: Record<string, string> = {
  trending: 'bg-red-100 text-red-700 border border-red-200',
  'en-cola': 'status-en-cola',
  pendiente: 'status-pendiente',
  contado: 'status-contado',
};

export default function GossipVotingFeed() {
  const [votes, setVotes] = useState<Record<string, number>>(
    Object.fromEntries(GOSSIP_DATA.map((g) => [g.id, g.votes]))
  );
  const [voted, setVoted] = useState<Record<string, boolean>>({});

  const handleVote = (id: string) => {
    if (voted[id]) return;
    setVotes((prev) => ({ ...prev, [id]: prev[id] + 1 }));
    setVoted((prev) => ({ ...prev, [id]: true }));
  };

  return (
    <section id="votar" className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-16">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={20} className="text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Chismes en Votación 🗳️</h2>
          </div>
          <p className="text-muted-foreground text-sm">Vota por el chisme que quieres escuchar primero</p>
        </div>
        <Link
          href="/"
          className="hidden sm:flex items-center gap-1.5 text-primary text-sm font-semibold hover:underline"
        >
          Ver todos →
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {GOSSIP_DATA.map((gossip) => (
          <div
            key={gossip.id}
            className={`bg-card border border-border rounded-2xl p-5 shadow-sm card-hover flex flex-col gap-3 ${
              gossip.status === 'trending' ? 'ring-2 ring-primary/20' : ''
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-2">
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[gossip.category] || categoryColors['Otro']}`}>
                {gossip.category}
              </span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${statusStyles[gossip.status]}`}>
                {gossip.statusLabel}
              </span>
            </div>

            {/* Gossip text */}
            <p className="text-sm text-foreground leading-relaxed flex-1 font-medium">
              {gossip.text}
            </p>

            {/* Footer */}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock size={11} />
                {gossip.time}
              </span>

              <button
                onClick={() => handleVote(gossip.id)}
                disabled={voted[gossip.id] || gossip.status === 'contado'}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all duration-150 ${
                  voted[gossip.id]
                    ? 'bg-primary/10 text-primary cursor-default'
                    : gossip.status === 'contado' ?'bg-muted text-muted-foreground cursor-default' :'bg-primary text-white hover:bg-primary/90 active:scale-95 shadow-sm'
                }`}
              >
                <ChevronUp
                  size={15}
                  className={voted[gossip.id] ? 'text-primary' : ''}
                />
                <span className="font-tabular">{votes[gossip.id]}</span>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Submit gossip CTA */}
      <div id="enviar" className="mt-10 text-center">
        <Link
          href="/home-landing-screen"
          className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-2xl text-base font-bold shadow-lg"
        >
          🤫 ¿Tienes un chisme? ¡Cuéntalo!
        </Link>
      </div>
    </section>
  );
}